import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataLabel, fmtDate, daysUntil, urgencyClass } from "@/lib/utils-ui";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import ExportMenu from "@/components/ExportMenu";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function VendorsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ name: "", contact_name: "", email: "", phone: "", services: "", amc_active: false, amc_expiry: "", company_id: "" });

  const load = async () => { const { data } = await api.get("/vendors"); setItems(data); };
  useEffect(() => { load(); api.get("/companies").then(r => setCompanies(r.data)); }, []);

  const create = async () => {
    try {
      const payload = { ...form, services: typeof form.services === "string" ? form.services.split(",").map(s => s.trim()).filter(Boolean) : form.services };
      if (user.role !== "super_admin") delete payload.company_id;
      if (!payload.amc_expiry) delete payload.amc_expiry;
      if (editing) {
        await api.put(`/vendors/${editing.id}`, payload);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", payload);
        toast.success("Vendor added");
      }
      setOpen(false); setEditing(null); load();
      setForm({ name: "", contact_name: "", email: "", phone: "", services: "", amc_active: false, amc_expiry: "", company_id: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (v) => {
    setEditing(v);
    setForm({
      name: v.name, contact_name: v.contact_name || "", email: v.email || "", phone: v.phone || "",
      services: (v.services || []).join(", "), amc_active: !!v.amc_active, amc_expiry: v.amc_expiry || "",
      company_id: v.company_id,
    });
    setOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/vendors/${deleting.id}`);
      toast.success("Vendor deleted");
      setDeleting(null); load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="vendors-page">
      <div className="flex items-start justify-between">
        <div>
          <DataLabel>Module 06</DataLabel>
          <h1 className="font-head font-black uppercase text-4xl tracking-tighter mt-1">Vendors & AMC</h1>
          <p className="text-sm text-muted-foreground mt-1">Contractors, maintenance agreements, renewal dates.</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu module="vendors" />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild><Button data-testid="new-vendor-btn" onClick={() => { setEditing(null); setForm({ name: "", contact_name: "", email: "", phone: "", services: "", amc_active: false, amc_expiry: "", company_id: "" }); }} className="bg-black text-white hover:bg-[#FF5000] h-10 uppercase tracking-wider text-xs font-head"><Plus size={14} className="mr-1"/>New Vendor</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-head uppercase tracking-tight">{editing ? "Edit Vendor" : "Register Vendor"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {user.role === "super_admin" && (
                <div className="col-span-2"><Label className="data-label">Company</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({...form, company_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                    <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2"><Label className="data-label">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})}/></div>
              <div><Label className="data-label">Contact</Label><Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})}/></div>
              <div><Label className="data-label">Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})}/></div>
              <div><Label className="data-label">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}/></div>
              <div><Label className="data-label">AMC Expiry</Label><Input type="date" value={form.amc_expiry} onChange={e => setForm({...form, amc_expiry: e.target.value, amc_active: !!e.target.value})}/></div>
              <div className="col-span-2"><Label className="data-label">Services (comma sep)</Label><Input value={form.services} onChange={e => setForm({...form, services: e.target.value})}/></div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-black text-white hover:bg-[#FF5000]">{editing ? "Save" : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <ConfirmDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete vendor?" description={deleting?.name} confirmLabel="Delete" variant="destructive"
        onConfirm={confirmDelete} testid="delete-vendor" />

      <div className="border border-border bg-white overflow-x-auto" data-testid="vendor-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-muted">
            {["VENDOR","CONTACT","SERVICES","AMC","AMC EXPIRY","ACTIONS"].map(h => <th key={h} className="text-left p-3 data-label">{h}</th>)}
          </tr></thead>
          <tbody>
            {items.map(v => {
              const d = daysUntil(v.amc_expiry);
              return (
                <tr key={v.id} className="border-b border-border hover-row" data-testid={`vendor-row-${v.id}`}>
                  <td className="p-3 font-semibold">{v.name}</td>
                  <td className="p-3 text-xs">{v.contact_name || "—"}{v.email && <div className="font-mono text-[10px] text-muted-foreground">{v.email}</div>}</td>
                  <td className="p-3 text-xs">{(v.services || []).join(", ") || "—"}</td>
                  <td className="p-3"><span className={`text-[10px] uppercase px-2 py-0.5 font-semibold ${v.amc_active ? "bg-[#E8F4EC] text-[#0a7a3f]" : "bg-muted text-muted-foreground"}`}>{v.amc_active ? "ACTIVE" : "INACTIVE"}</span></td>
                  <td className="p-3 font-mono text-xs">{v.amc_expiry ? <span className={urgencyClass(d)}>{fmtDate(v.amc_expiry)} · {d}D</span> : "—"}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <button data-testid={`edit-vendor-${v.id}`} onClick={() => startEdit(v)} title="Edit" className="hover:text-[#FF5000]"><Pencil size={14}/></button>
                      <button data-testid={`delete-vendor-${v.id}`} onClick={() => setDeleting(v)} title="Delete" className="hover:text-[#FF3B30]"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No vendors.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
